import React from 'react';
import { motion } from 'framer-motion';
import {
    FaLightbulb, FaHandshake, FaRocket, FaGlobe, FaQuoteLeft,
    FaCheckCircle
} from 'react-icons/fa';
import heroImage from '../assets/d19a8f50f70944b0c4b2fdf4553d2462.jpg';
import ownerImage from '../assets/WhatsApp Image 2026-02-09 at 8.41.13 PM.jpeg';

const AboutPage = () => {
    return (
        <div className="bg-white font-sans text-slate-900">
            {/* 1. Hero Section - Matching Height & Stronger Overlay */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    {/* Stronger Gradient Overlay for Text Visibility */}
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-slate-900/30"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl text-white"
                    >
                        <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
                            Innovating for a <br /><span className="text-cyan-400">Smarter Future</span>
                        </h1>
                        <p className="md:text-xl text-slate-200 leading-relaxed font-light max-w-2xl text-lg">
                            We bridge the gap between business needs and technological possibilities to enable enterprises to thrive.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* 2. Our Mission - Clean Light Section */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 font-bold text-xs uppercase tracking-wider rounded-full mb-4">
                                Who We Are
                            </div>
                            <h2 className="text-3xl font-bold text-slate-900 mb-6 leading-tight">
                                Architects of Digital Transformation
                            </h2>
                            <p className="text-slate-600 mb-6 leading-relaxed">
                                Tech-Inovant is a global technology consultancy dedicated to solving complex business challenges. We don't just write code; we build customized solutions that drive tangible growth and long-term success for our partners.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-2">
                                    <FaCheckCircle className="text-cyan-500" />
                                    <span className="font-medium text-slate-700">Client-Centric</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <FaCheckCircle className="text-cyan-500" />
                                    <span className="font-medium text-slate-700">Innovation-Driven</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <FaCheckCircle className="text-cyan-500" />
                                    <span className="font-medium text-slate-700">Global Standards</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <FaCheckCircle className="text-cyan-500" />
                                    <span className="font-medium text-slate-700">Future-Ready</span>
                                </div>
                            </div>
                        </div>
                        <div className="grid gap-6">
                            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 hover:shadow-lg transition-shadow">
                                <FaLightbulb className="text-3xl text-blue-600 mb-3" />
                                <h3 className="text-lg font-bold text-slate-900 mb-2">Our Vision</h3>
                                <p className="text-slate-600 text-sm">To be the most trusted technology partner for automated businesses worldwide.</p>
                            </div>
                            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 hover:shadow-lg transition-shadow">
                                <FaHandshake className="text-3xl text-cyan-600 mb-3" />
                                <h3 className="text-lg font-bold text-slate-900 mb-2">Our Mission</h3>
                                <p className="text-slate-600 text-sm">Empowering organizations through scalable, secure, and intelligent digital solutions.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. Leadership Spotlight (Founder) - High Contrast Light Section */}
            <section className="py-16 bg-slate-50 text-slate-900 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                    <div className="flex flex-col md:flex-row items-center gap-12">
                        {/* Image Section - Constrained Width/Height */}
                        <div className="md:w-5/12 relative flex justify-center">
                            <div className="relative max-w-sm w-full">
                                <div className="absolute inset-0 bg-cyan-200 rounded-lg transform translate-x-3 translate-y-3"></div>
                                <img
                                    src={ownerImage}
                                    alt="Abhimanyu Kumar Singh"
                                    className="relative rounded-lg shadow-xl w-full h-[400px] object-cover object-top border border-slate-200"
                                />
                            </div>
                        </div>

                        {/* Content Section - Dark Text for Visibility */}
                        <div className="md:w-7/12">
                            <h2 className="text-3xl md:text-4xl font-bold mb-1 text-slate-900">Abhimanyu Kumar Singh</h2>
                            <p className="text-cyan-600 font-bold text-sm uppercase tracking-widest mb-8">Owner & Founder</p>

                            <FaQuoteLeft className="text-3xl text-slate-300 mb-4" />
                            <blockquote className="text-lg md:text-xl font-medium text-slate-700 leading-relaxed italic mb-8 pl-4 border-l-4 border-cyan-500">
                                "Technology is not just a tool; it's the catalyst for human potential. At Tech-Inovant, we are committed to building digital ecosystems that empower businesses to lead, innovate, and inspire."
                            </blockquote>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex items-center gap-3 text-slate-700">
                                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                                        <FaRocket className="text-cyan-600" />
                                    </div>
                                    <span className="text-sm font-bold">Visionary Leadership</span>
                                </div>
                                <div className="flex items-center gap-3 text-slate-700">
                                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                                        <FaGlobe className="text-cyan-600" />
                                    </div>
                                    <span className="text-sm font-bold">Global Strategy</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. Stats Bar */}
            <section className="py-12 bg-blue-600">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-blue-500/50">
                        <div>
                            <div className="text-4xl font-bold text-white mb-1">5+</div>
                            <div className="text-blue-100 text-xs uppercase tracking-wider">Years</div>
                        </div>
                        <div>
                            <div className="text-4xl font-bold text-white mb-1">100+</div>
                            <div className="text-blue-100 text-xs uppercase tracking-wider">Projects</div>
                        </div>
                        <div>
                            <div className="text-4xl font-bold text-white mb-1">50+</div>
                            <div className="text-blue-100 text-xs uppercase tracking-wider">Clients</div>
                        </div>
                        <div>
                            <div className="text-4xl font-bold text-white mb-1">24/7</div>
                            <div className="text-blue-100 text-xs uppercase tracking-wider">Support</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. Minimal CTA */}
            <section className="py-16 bg-white text-center">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-slate-900 mb-6">Ready to Innovate?</h2>
                    <p className="text-slate-600 mb-8">Join us in shaping the future of technology.</p>
                    <div className="flex justify-center gap-4">
                        <a href="/contact" className="px-8 py-3 bg-blue-600 text-white font-bold rounded hover:bg-blue-700 transition-colors uppercase text-sm tracking-wide shadow-lg">
                            Get in Touch
                        </a>
                        <a href="/careers" className="px-8 py-3 bg-white text-slate-900 font-bold border border-slate-300 rounded hover:bg-slate-100 transition-colors uppercase text-sm tracking-wide">
                            Join Team
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;
